/**
 * Twin of Containd `types/DefaultTitleStyle.ts` for GenericItemHost paint.
 * Host cannot import this package; keep parse/resolve behavior aligned.
 */

export type TitleHorizontalAlign = 'left' | 'center' | 'right';
export type TitleVerticalAlign = 'top' | 'center' | 'bottom';
/** Canvas raster thumbnail fit; default cover. `fill` stretches (may distort). */
export type TitleThumbnailFit = 'cover' | 'contain' | 'fill';

export interface DefaultTitleStyleSettings {
  horizontalAlign?: TitleHorizontalAlign;
  verticalAlign?: TitleVerticalAlign;
  paddingHorizontal?: number;
  paddingVertical?: number;
  backdropOpacity?: number;
  textboxOpacity?: number;
  bold?: boolean;
  italic?: boolean;
  strikethrough?: boolean;
  underlined?: boolean;
  underlineStrokeWidth?: number;
  maxLines?: number;
  lineHeight?: number;
  showChildrenCount?: boolean;
  /** Canvas raster thumbnail fit; default cover. `fill` stretches (may distort). */
  thumbnailFit?: TitleThumbnailFit;
}

export const DEFAULT_TITLE_STYLE: Required<
  Pick<
    DefaultTitleStyleSettings,
    | 'horizontalAlign'
    | 'verticalAlign'
    | 'paddingHorizontal'
    | 'paddingVertical'
    | 'backdropOpacity'
    | 'textboxOpacity'
    | 'bold'
    | 'italic'
    | 'strikethrough'
    | 'underlined'
    | 'maxLines'
    | 'lineHeight'
    | 'thumbnailFit'
  >
> = {
  horizontalAlign: 'center',
  verticalAlign: 'center',
  paddingHorizontal: 3,
  paddingVertical: 3,
  backdropOpacity: 0.5,
  textboxOpacity: 1,
  bold: true,
  italic: false,
  strikethrough: false,
  underlined: false,
  /** 0 = geometry-only (item height); no fixed 2-line default. */
  maxLines: 0,
  lineHeight: 1.2,
  thumbnailFit: 'cover',
};

const HORIZONTAL_ALIGN = new Set<TitleHorizontalAlign>(['left', 'center', 'right']);
const VERTICAL_ALIGN = new Set<TitleVerticalAlign>(['top', 'center', 'bottom']);
const THUMBNAIL_FIT = new Set<TitleThumbnailFit>(['cover', 'contain', 'fill']);

/** Map product thumbnailFit to CSS `object-fit` (`fill` stretches, unlike SVG fill). */
export function thumbnailFitToObjectFit(
  fit: TitleThumbnailFit | undefined,
): 'cover' | 'contain' | 'fill' {
  if (fit === 'contain') return 'contain';
  if (fit === 'fill') return 'fill';
  return 'cover';
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function readOptionalNumber(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }
  return value;
}

function readOptionalBoolean(value: unknown): boolean | undefined {
  if (typeof value !== 'boolean') {
    return undefined;
  }
  return value;
}

function normalizePartial(raw: Record<string, unknown>): DefaultTitleStyleSettings {
  const result: DefaultTitleStyleSettings = {};

  if (typeof raw.horizontalAlign === 'string' && HORIZONTAL_ALIGN.has(raw.horizontalAlign as TitleHorizontalAlign)) {
    result.horizontalAlign = raw.horizontalAlign as TitleHorizontalAlign;
  }
  if (typeof raw.verticalAlign === 'string' && VERTICAL_ALIGN.has(raw.verticalAlign as TitleVerticalAlign)) {
    result.verticalAlign = raw.verticalAlign as TitleVerticalAlign;
  }
  if (typeof raw.thumbnailFit === 'string' && THUMBNAIL_FIT.has(raw.thumbnailFit as TitleThumbnailFit)) {
    result.thumbnailFit = raw.thumbnailFit as TitleThumbnailFit;
  }

  const paddingHorizontal = readOptionalNumber(raw.paddingHorizontal);
  if (paddingHorizontal !== undefined && paddingHorizontal >= 0) {
    result.paddingHorizontal = paddingHorizontal;
  }
  const paddingVertical = readOptionalNumber(raw.paddingVertical);
  if (paddingVertical !== undefined && paddingVertical >= 0) {
    result.paddingVertical = paddingVertical;
  }

  const backdropOpacity = readOptionalNumber(raw.backdropOpacity);
  if (backdropOpacity !== undefined) {
    result.backdropOpacity = clamp01(backdropOpacity);
  }
  const textboxOpacity = readOptionalNumber(raw.textboxOpacity);
  if (textboxOpacity !== undefined) {
    result.textboxOpacity = clamp01(textboxOpacity);
  }

  const bold = readOptionalBoolean(raw.bold);
  if (bold !== undefined) result.bold = bold;
  const italic = readOptionalBoolean(raw.italic);
  if (italic !== undefined) result.italic = italic;
  const strikethrough = readOptionalBoolean(raw.strikethrough);
  if (strikethrough !== undefined) result.strikethrough = strikethrough;
  const underlined = readOptionalBoolean(raw.underlined);
  if (underlined !== undefined) result.underlined = underlined;

  const underlineStrokeWidth = readOptionalNumber(raw.underlineStrokeWidth);
  if (underlineStrokeWidth !== undefined && underlineStrokeWidth > 0) {
    result.underlineStrokeWidth = underlineStrokeWidth;
  }

  const maxLines = readOptionalNumber(raw.maxLines);
  if (maxLines !== undefined && maxLines >= 0) {
    result.maxLines = Math.floor(maxLines);
  }

  const lineHeight = readOptionalNumber(raw.lineHeight);
  if (lineHeight !== undefined && lineHeight > 0) {
    result.lineHeight = lineHeight;
  }

  const showChildrenCount = readOptionalBoolean(raw.showChildrenCount);
  if (showChildrenCount !== undefined) {
    result.showChildrenCount = showChildrenCount;
  }

  return result;
}

export function parseDefaultTitleStyle(raw?: string | null): DefaultTitleStyleSettings | null {
  if (raw == null || raw.trim().length === 0) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (typeof parsed !== 'object' || parsed == null || Array.isArray(parsed)) {
    return null;
  }

  const normalized = normalizePartial(parsed as Record<string, unknown>);
  return Object.keys(normalized).length > 0 ? normalized : null;
}

export interface ResolvedDefaultTitleStyle {
  horizontalAlign: TitleHorizontalAlign;
  verticalAlign: TitleVerticalAlign;
  paddingHorizontal: number;
  paddingVertical: number;
  backdropOpacity: number;
  textboxOpacity: number;
  bold: boolean;
  italic: boolean;
  strikethrough: boolean;
  underlined: boolean;
  underlineStrokeWidth: number | null;
  maxLines: number | null;
  lineHeight: number;
  showChildrenCount: boolean | undefined;
  thumbnailFit: TitleThumbnailFit;
}

export function resolveTitleTextDecorationLine(
  underlined: boolean,
  strikethrough: boolean,
  useFontUnderline: boolean,
): 'none' | 'underline' | 'line-through' | 'underline line-through' {
  if (underlined && useFontUnderline && strikethrough) {
    return 'underline line-through';
  }
  if (underlined && useFontUnderline) {
    return 'underline';
  }
  if (strikethrough) {
    return 'line-through';
  }
  return 'none';
}

export function resolveDefaultTitleStyle(
  raw?: string | null,
  options?: { hasThumbnail?: boolean },
): ResolvedDefaultTitleStyle {
  const parsed = parseDefaultTitleStyle(raw);
  const hasThumbnail = options?.hasThumbnail === true;
  const backdropOpacity = parsed?.backdropOpacity ?? (hasThumbnail ? DEFAULT_TITLE_STYLE.backdropOpacity : 1);

  return {
    horizontalAlign: parsed?.horizontalAlign ?? DEFAULT_TITLE_STYLE.horizontalAlign,
    verticalAlign: parsed?.verticalAlign ?? DEFAULT_TITLE_STYLE.verticalAlign,
    paddingHorizontal: parsed?.paddingHorizontal ?? DEFAULT_TITLE_STYLE.paddingHorizontal,
    paddingVertical: parsed?.paddingVertical ?? DEFAULT_TITLE_STYLE.paddingVertical,
    backdropOpacity,
    textboxOpacity: parsed?.textboxOpacity ?? DEFAULT_TITLE_STYLE.textboxOpacity,
    bold: parsed?.bold ?? DEFAULT_TITLE_STYLE.bold,
    italic: parsed?.italic ?? DEFAULT_TITLE_STYLE.italic,
    strikethrough: parsed?.strikethrough ?? DEFAULT_TITLE_STYLE.strikethrough,
    underlined: parsed?.underlined ?? DEFAULT_TITLE_STYLE.underlined,
    underlineStrokeWidth:
      parsed?.underlineStrokeWidth != null && parsed.underlineStrokeWidth > 0
        ? parsed.underlineStrokeWidth
        : null,
    maxLines: (() => {
      const raw = parsed?.maxLines ?? DEFAULT_TITLE_STYLE.maxLines;
      // 0 / unset default → null (geometry clamps; Edit Entry still stores 0 as unlimited).
      return raw === 0 ? null : raw;
    })(),
    lineHeight: parsed?.lineHeight ?? DEFAULT_TITLE_STYLE.lineHeight,
    showChildrenCount: parsed?.showChildrenCount,
    thumbnailFit: parsed?.thumbnailFit ?? DEFAULT_TITLE_STYLE.thumbnailFit,
  };
}
